/**
 * 成果价值评估新版配置（设计稿 成果价值评估_slices）
 * 图片统一走 value_assessment_new/aliases 短英文名
 *
 * 切图映射（aliases ← 成果价值评估_slices）:
 * banner-bg ← beijing@2x
 * title-mark ← 成果价值评估@2x
 * card-cover-fallback ← 图层 1@2x
 * icon-arrow ← 图层 2@2x
 * icon-field ← 领域服务@2x
 * icon-time ← 时间@2x
 * icon-score ← 评分  收藏-01@2x
 * icon-maturity ← 程度@2x
 * system-card-standard ← 组 17@2x
 * system-card-model ← 组 17@2x(1)
 * system-card-3d ← 组 17@2x(2)
 * system-card-indicator ← 组 17@2x(3)
 * system-icon-standard ← 定密依据@2x
 * system-icon-model ← 爆炸模式@2x
 * system-icon-3d ← 标识模型@2x
 * system-icon-indicator ← 评价@2x@2x
 * name-deco ← 矩形 44@2x
 * score-badge ← 矩形 903@2x
 */
import { deepCloneConfig } from '@/utils/deepCloneConfig'
import { applyPortalZhToRu } from '@/utils/applyPortalZhToRu'
import { getCurrentLocale } from '@/utils/i18nText'
import { L, resolveLocaleImages } from '@/utils/localeAsset'

const A = (name) => L(`value_assessment_new/aliases/${name}`)
const Nav = (name) => L(`home_new/aliases/${name}`)

const zhCN = {
  siteInfo: {
    title: '杨凌农业科技成果转化综合信息服务平台',
    logo: L('shouye/web-logo.png'),
    loginText: '登录',
    registerText: '注册',
    logoutText: '退出'
  },
  breadcrumb: {
    home: '首页',
    current: '成果价值评估'
  },
  header: {
    titleMain1: '成果价值',
    titleMain2: '评估',
    titleSub: '多元评估 立体转化',
    searchPlaceholder: '搜索成果名称',
    searchButton: '搜索',
    bannerBg: A('banner-bg.png'),
    titleMark: A('title-mark.png')
  },
  modules: [
    { id: 'home', name: '首页', enName: 'HOME', icon: Nav('nav-home.png'), link: '/index' },
    { id: 'innovation', name: '走进杨凌', enName: 'YANGLING', icon: Nav('nav-innovation.png'), link: '/innovation' },
    { id: 'supply', name: '供需中心', enName: 'SUPPLY & DEMAND', icon: Nav('nav-supply.png'), link: '/supply-demand' },
    { id: 'evaluation', name: '价值评估', enName: 'EVALUATE', icon: Nav('nav-evaluation.png'), link: '/value-assessment' },
    { id: 'manager', name: '技术经理人', enName: 'MANAGER', icon: Nav('nav-manager.png'), link: '/manager' },
    { id: 'transaction', name: '交易中心', enName: 'TRANSACTION', icon: Nav('nav-transaction.png'), link: '/transaction' },
    { id: 'service', name: '科创服务', enName: 'SERVICE', icon: Nav('nav-service.png'), link: '/innovation-service' },
    { id: 'talent', name: '科技人才', enName: 'TALENT', icon: Nav('nav-talent.png'), link: '/talent' },
    { id: 'enterprise', name: '农业企业', enName: 'ENTERPRISE', icon: Nav('nav-enterprise.png'), link: '/agriculture-enterprise' }
  ],
  systemSection: {
    title: '价值评估体系',
    subtitle: 'VALUE ASSESSMENT SYSTEM',
    nameDeco: A('name-deco.png'),
    items: [
      {
        key: 'standard',
        title: '标准依据',
        desc: '国标GB/T39057-2020\n《科技成果经济价值评估指南》',
        bg: A('system-card-standard.png'),
        icon: A('system-icon-standard.png')
      },
      {
        key: 'model',
        title: '评估模型',
        desc: '中国农村技术开发中心开发\n“农业科技成果价值评估系统V1.0”',
        bg: A('system-card-model.png'),
        icon: A('system-icon-model.png')
      },
      {
        key: 'scene',
        title: '三维评估体系',
        desc: '技术场景\n产业场景\n商业场景',
        bg: A('system-card-3d.png'),
        icon: A('system-icon-3d.png')
      },
      {
        key: 'indicator',
        title: '评价指标体系',
        desc: '3个一级指标-核心维度\n18个二级指标-细分领域\n62个三级指标-具体评估项',
        bg: A('system-card-indicator.png'),
        icon: A('system-icon-indicator.png')
      }
    ]
  },
  icons: {
    field: A('icon-field.png'),
    time: A('icon-time.png'),
    score: A('icon-score.png'),
    maturity: A('icon-maturity.png'),
    arrow: A('icon-arrow.png')
  },
  fieldFilter: {
    label: '成果领域',
    allLabel: '全部'
  },
  timeFilter: {
    label: '时间',
    allLabel: '全部'
  },
  scoreFilter: {
    label: '评分',
    allLabel: '全部'
  },
  maturityFilter: {
    label: '成熟度',
    allLabel: '全部'
  },
  card: {
    scoreUnit: '分',
    viewDetail: '查看详情',
    emptyTip: '待接口提供',
    coverFallback: L('default.png'),
    arrowIcon: A('icon-arrow.png'),
    scoreBadge: A('score-badge.png')
  },
  pagination: {
    pageInfo: '共{total}条，每页显示{pageSize}条',
    prevPage: '上一页',
    nextPage: '下一页',
    loading: '加载中...'
  },
  footer: {
    copyright: '© 2026 杨凌农业科技成果转化综合信息服务平台 版权所有',
    support: '技术支持：浪潮软件'
  }
}

const enUS = {
  siteInfo: {
    title: 'Yangling Agricultural Science & Technology Achievement Transformation Platform',
    logo: L('shouye/web-logo.png'),
    loginText: 'Login',
    registerText: 'Register',
    logoutText: 'Logout'
  },
  breadcrumb: {
    home: 'Home',
    current: 'Value Assessment'
  },
  header: {
    titleMain1: 'Value',
    titleMain2: 'Assessment',
    titleSub: 'Multi-dimensional assessment, integrated transformation',
    searchPlaceholder: 'Search achievement name',
    searchButton: 'Search',
    bannerBg: A('banner-bg.png'),
    titleMark: A('title-mark.png')
  },
  modules: [
    { id: 'home', name: 'Home', enName: 'HOME', icon: Nav('nav-home.png'), link: '/index' },
    { id: 'innovation', name: 'Yangling', enName: 'YANGLING', icon: Nav('nav-innovation.png'), link: '/innovation' },
    { id: 'supply', name: 'Supply & Demand', enName: 'SUPPLY & DEMAND', icon: Nav('nav-supply.png'), link: '/supply-demand' },
    { id: 'evaluation', name: 'Evaluation', enName: 'EVALUATE', icon: Nav('nav-evaluation.png'), link: '/value-assessment' },
    { id: 'manager', name: 'Managers', enName: 'MANAGER', icon: Nav('nav-manager.png'), link: '/manager' },
    { id: 'transaction', name: 'Transaction', enName: 'TRANSACTION', icon: Nav('nav-transaction.png'), link: '/transaction' },
    { id: 'service', name: 'Services', enName: 'SERVICE', icon: Nav('nav-service.png'), link: '/innovation-service' },
    { id: 'talent', name: 'Talent', enName: 'TALENT', icon: Nav('nav-talent.png'), link: '/talent' },
    { id: 'enterprise', name: 'Enterprises', enName: 'ENTERPRISE', icon: Nav('nav-enterprise.png'), link: '/agriculture-enterprise' }
  ],
  systemSection: {
    title: 'Assessment System',
    subtitle: 'VALUE ASSESSMENT SYSTEM',
    nameDeco: A('name-deco.png'),
    items: [
      {
        key: 'standard',
        title: 'Standard Basis',
        desc: 'GB/T39057-2020\nGuidelines for Economic Value Assessment of S&T Achievements',
        bg: A('system-card-standard.png'),
        icon: A('system-icon-standard.png')
      },
      {
        key: 'model',
        title: 'Evaluation Model',
        desc: 'Agricultural S&T Achievement\nValue Assessment System V1.0',
        bg: A('system-card-model.png'),
        icon: A('system-icon-model.png')
      },
      {
        key: 'scene',
        title: '3D Evaluation System',
        desc: 'Technology scenario\nIndustry scenario\nBusiness scenario',
        bg: A('system-card-3d.png'),
        icon: A('system-icon-3d.png')
      },
      {
        key: 'indicator',
        title: 'Indicator System',
        desc: '3 primary indicators\n18 secondary indicators\n62 tertiary indicators',
        bg: A('system-card-indicator.png'),
        icon: A('system-icon-indicator.png')
      }
    ]
  },
  icons: zhCN.icons,
  fieldFilter: {
    label: 'Field',
    allLabel: 'All'
  },
  timeFilter: {
    label: 'Time',
    allLabel: 'All'
  },
  scoreFilter: {
    label: 'Score',
    allLabel: 'All'
  },
  maturityFilter: {
    label: 'Maturity',
    allLabel: 'All'
  },
  card: {
    scoreUnit: 'pts',
    viewDetail: 'View Details',
    emptyTip: '待接口提供',
    coverFallback: L('default.png'),
    arrowIcon: A('icon-arrow.png'),
    scoreBadge: A('score-badge.png')
  },
  pagination: {
    pageInfo: '{total} items, {pageSize} per page',
    prevPage: 'Prev',
    nextPage: 'Next',
    loading: 'Loading...'
  },
  footer: {
    copyright: '© 2026 Yangling Agricultural Science & Technology Marketplace. All rights reserved.',
    support: 'Technical support: Inspur Software'
  }
}

export function getValueAssessmentNewConfig(locale = getCurrentLocale()) {
  let raw
  if (locale === 'en-US') raw = deepCloneConfig(enUS)
  else if (locale === 'ru-RU') raw = applyPortalZhToRu(deepCloneConfig(zhCN))
  else raw = deepCloneConfig(zhCN)
  return resolveLocaleImages(raw, locale)
}
